import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  Layers,
  BookOpen,
  Wrench,
  ArrowUpRight,
} from "lucide-react";
import { track } from "@/lib/analytics";

/* -------- Content -------- */

const VALUE_CARDS = [
  {
    icon: Layers,
    title: "Complete",
    body: "One career, end to end, from how the industry works to your first offer.",
  },
  {
    icon: BookOpen,
    title: "Practical",
    body: "Built around real recruiting, interviews and deal work, not theory.",
  },
  {
    icon: Wrench,
    title: "Tools included",
    body: "Trackers, templates and case studies come with every Playbook.",
  },
];

// Live playbooks — user will insert real Gumroad (USD) and Payhip (INR) URLs later.
// `anchor_id` is the stable HTML id used for deep-linking from Career Hub CTAs.
const LIVE_PLAYBOOKS = [
  {
    slug: "investment-banking-playbook",
    anchor_id: "investment-banking-playbook",
    title: "The Ultimate Investment Banking Playbook",
    description:
      "A practical roadmap for breaking into investment banking — from understanding the industry to preparing for interviews and building the skills recruiters actually value.",
    contents: [
      "125+ page Playbook",
      "IB Deal & Case Study Pack",
      "IB Analyst Work Simulation — PDF",
      "IB Networking & Outreach Pack",
      "IB Networking Tracker — Excel",
      "IB Deal & Market Tracker — Excel",
      "IB Analyst Work Simulation — Excel Workbook",
    ],
    usd_url: "https://vantstride.gumroad.com/l/oirrt",
    inr_url: "https://payhip.com/b/emsSl",
  },
  {
    slug: "private-equity-playbook-live",
    anchor_id: "private-equity-playbook",
    title: "The Private Equity Playbook",
    description:
      "For aspiring PE professionals: how deals work, how investors think, and how LBOs and investment theses come together, plus the technical and interview preparation you need to move from investment banking into PE.",
    contents: [
      "150+ page Playbook",
      "PE Research & Due Diligence Playbook",
      "PE Deal Case Study",
      "Recruiting Outreach Pack",
      "PE Networking Recruiting Tracker",
      "Investment Thesis IC Memo Template",
      "PE Investment Deal Tracker",
    ],
    usd_url: "https://vantstride.gumroad.com/l/omzrcu",
    inr_url: "https://payhip.com/b/dbzyO",
  },
  {
    slug: "energy-industry-playbook",
    anchor_id: "energy-finance-playbook",
    title: "The Ultimate Energy Finance Playbook",
    description:
      "Learn how oil, gas, LNG and renewables businesses work, how to value and model them, and how to prepare for energy finance interviews and careers.",
    contents: [
      "170+ page Playbook",
      "Research & Data Resource Guide",
      "Case Study & Project Pack",
      "Case Pack Workbook",
      "Modelling Pack",
      "Energy Finance Tracker",
      "Networking Tracker",
      "Career & Interview Toolkit",
    ],
    usd_url: "https://vantstride.gumroad.com/l/wvdyop",
    inr_url: "https://payhip.com/b/l3joL",
  },
];

// Coming-soon playbook slugs — display only. No signup collection.
const COMING_SOON_PLAYBOOKS = [
  {
    slug: "venture-capital-playbook",
    title: "Venture Capital Playbook",
    description:
      "Helping aspiring investors understand startup investing, venture deals, fundraising, portfolio strategy, and careers inside venture capital.",
  },
  {
    slug: "hedge-funds-playbook",
    title: "Hedge Funds Playbook",
    description:
      "Explore hedge fund investing, public markets, research processes, portfolio construction, and hedge fund recruiting.",
  },
  {
    slug: "real-estate-finance-playbook",
    title: "Real Estate Finance Playbook",
    description:
      "Understand commercial real estate investing, acquisitions, development, underwriting, and real estate financial modeling.",
  },
];

/* -------- Page -------- */

export default function Playbooks() {
  const location = useLocation();

  // Fire a single playbook_view when the Playbooks page loads.
  useEffect(() => {
    track("playbook_view", { playbook_slug: "playbooks_index" });
  }, []);

  // Route-aware scroll to the matching Playbook when arriving with a hash
  // (e.g. /playbooks#investment-banking-playbook). We defer with rAF so the
  // cards are mounted before we try to scroll.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      {/* Hero */}
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
          VantStride Playbooks
        </div>
        <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-[#0A2540] font-medium mt-3">
          We give you the map first.
        </h1>
        <p className="mt-4 text-slate-600 text-lg leading-relaxed">
          Before you spend hundreds of hours figuring out what to learn,
          understand the career, the industry, the skills, and the path
          ahead—all in one structured roadmap.
        </p>
      </div>

      {/* Value cards */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
        {VALUE_CARDS.map((p) => (
          <div
            key={p.title}
            className="rounded-2xl border border-slate-100 bg-white px-5 py-4"
            data-testid={`playbook-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="w-7 h-7 rounded-lg bg-[#0A2540] text-white grid place-items-center">
              <p.icon className="w-3.5 h-3.5" />
            </div>
            <div className="mt-2 font-display text-base text-[#0A2540] font-medium">{p.title}</div>
            <div className="mt-0.5 text-[13px] text-slate-600 leading-snug">{p.body}</div>
          </div>
        ))}
      </div>

      {/* Free guide callout */}
      <div
        className="mt-8 rounded-3xl bg-[#0A2540] text-white px-6 py-7 sm:px-10 sm:py-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
        data-testid="playbooks-free-guide"
      >
        <div className="lg:max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            Free · VantStride Guide
          </div>
          <h2 className="mt-2 font-display text-xl sm:text-2xl tracking-tight font-medium">
            Finance Interview Foundations Guide
          </h2>
          <p className="mt-2 text-sm text-slate-300 leading-relaxed">
            Build your finance interview foundation before you go deeper. A free 38-page guide
            covering technical fundamentals, career paths, interview preparation, 112 practice
            questions, and a 14-day preparation system.
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-start lg:items-end gap-2">
          <a
            href="https://payhip.com/b/Eyemc"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="playbooks-free-guide-cta"
            className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-100 transition-colors active:scale-95"
          >
            Get the Free Guide
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <span className="text-[11px] text-slate-400">Free download · 2026 Edition</span>
        </div>
      </div>

      {/* Live playbooks */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-[#0A2540] font-medium">
            Live Playbooks
          </h2>
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">
            Available now
          </span>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {LIVE_PLAYBOOKS.map((pb, i) => (
            <LivePlaybookCard key={pb.slug} pb={pb} idx={i} />
          ))}
        </div>
      </div>

      {/* Founder note */}
      <div className="mt-14 border-t border-slate-200 pt-10 max-w-3xl" data-testid="playbooks-founder-note">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
          A Note from the Founder
        </div>
        <h2 className="mt-2 font-display text-xl sm:text-2xl tracking-tight text-[#0A2540] font-medium">
          Built to make finance careers easier to navigate.
        </h2>
        <p className="mt-3 text-slate-600 leading-relaxed">
          I built VantStride because breaking into finance can feel harder than it should when the
          information you need is scattered everywhere. Each Playbook brings one career together in
          one place — how the industry works, which skills matter, and how to prepare for
          recruiting. My aim is simple: give you a clear path so you can spend more time preparing
          and less time searching.
        </p>
        <div className="mt-3">
          <p className="text-sm text-[#0A2540] font-medium">Chhavi Kalra</p>
          <p className="mt-0.5 text-xs text-slate-500 italic">Founder, VantStride</p>
        </div>
      </div>

      {/* Coming Soon playbooks */}
      <div className="mt-14">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-[#0A2540] font-medium">
            Coming Soon
          </h2>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {COMING_SOON_PLAYBOOKS.map((pb, i) => (
            <ComingSoonPlaybookCard
              key={pb.slug}
              pb={pb}
              idx={i}
            />
          ))}
        </div>
      </div>

      {/* Product Copyright & Usage Notice — shown once, page-wide */}
      <div className="mt-12 text-center" data-testid="playbook-copyright-notice">
        <p className="text-[11px] text-slate-400 leading-relaxed">
          © 2026 VantStride. All rights reserved.
        </p>
        <p className="mt-1.5 text-[11px] text-slate-400 leading-relaxed max-w-xl mx-auto">
          This product is licensed for individual use by the purchaser only.
          Redistribution, resale, uploading, sharing, reproduction, or
          distribution of this product or its contents without written
          permission from VantStride is prohibited.
        </p>
        <p className="mt-1.5 text-[11px] text-slate-400 leading-relaxed">
          See our{" "}
          <Link
            to="/copyright-and-intellectual-property"
            className="text-[#0A2540] font-medium underline underline-offset-2 hover:text-[#051A2E]"
          >
            Copyright &amp; Intellectual Property Policy
          </Link>
          .
        </p>
      </div>

      {/* Bottom CTA */}
      <div className="mt-20 rounded-3xl bg-[#0A2540] text-white p-10 sm:p-14">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-white/10 grid place-items-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
              More Playbooks are coming.
            </h2>
            <p className="mt-3 text-slate-200 max-w-2xl">
              We're continuously building new Playbooks across finance careers.
              Explore the live Career Hubs while the next releases are being finalised.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/roadmaps"
                data-testid="playbooks-cta-hubs"
                className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors"
              >
                Explore Career Hubs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- Cards -------- */

function LivePlaybookCard({ pb, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: idx * 0.05 }}
      className="rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col scroll-mt-24"
      id={pb.anchor_id}
      data-testid={`playbook-card-live-${pb.slug}`}
    >
      <h3 className="font-display text-2xl text-[#0A2540] font-medium leading-snug">
        {pb.title}
      </h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">{pb.description}</p>
      <div className="flex-1" />
      {pb.contents?.length > 0 && (
        <div
          data-testid={`playbook-contents-${pb.slug}`}
          className="mt-5 rounded-[10px] border border-[#0A2540]/25 px-4 py-3.5"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            What's Included
          </div>
          <ul className="mt-2 space-y-1">
            {pb.contents.map((c) => (
              <li
                key={c}
                className="flex items-start gap-2.5 text-xs text-[#0A2540] leading-snug"
              >
                <span
                  aria-hidden="true"
                  className="mt-[6px] h-[3px] w-[3px] shrink-0 rotate-45 bg-[#C6A15B]/80"
                />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4 w-full sm:max-w-[260px] flex flex-col gap-2">
        <a
          href={pb.inr_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track("playbook_purchase_intent", {
              playbook_slug: pb.slug,
              playbook_title: pb.title,
              currency: "INR",
            })
          }
          data-testid={`playbook-inr-${pb.slug}`}
          className="w-full inline-flex items-center justify-center rounded-full px-4 py-2.5 text-xs font-semibold bg-[#0A2540] text-white hover:bg-[#051A2E] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(10,37,64,0.2)] transition-all"
        >
          <span>India Access (INR) ↗</span>
        </a>
        <a
          href={pb.usd_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track("playbook_purchase_intent", {
              playbook_slug: pb.slug,
              playbook_title: pb.title,
              currency: "USD",
            })
          }
          data-testid={`playbook-usd-${pb.slug}`}
          className="w-full inline-flex items-center justify-center rounded-full px-4 py-2.5 text-xs font-semibold border border-slate-200 text-[#0A2540] bg-white hover:border-[#0A2540] transition-colors"
        >
          <span>Global Access ↗</span>
        </a>
      </div>
    </motion.div>
  );
}

function ComingSoonPlaybookCard({ pb, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: idx * 0.05 }}
      className="rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] flex flex-col"
      data-testid={`playbook-card-soon-${pb.slug}`}
    >
      <h3 className="font-display text-2xl text-[#0A2540] font-medium leading-snug">
        {pb.title}
      </h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{pb.description}</p>
    </motion.div>
  );
}
